import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useSpeedBackend } from '@/features/wallet';
import { queryKeys } from '@/lib/query';

/**
 * Agents whose builder approval this page load already tried to repair. Module level so a
 * remount cannot re-fire it; the backend's attempt counter and cooldown bound the rest.
 */
const requestedForAgents = new Set<string>();

/** test-only */
export function resetBuilderFeeApprovals(): void {
    requestedForAgents.clear();
}

export interface UseBuilderFeeApprovalInput {
    builderApprovalStale: boolean;
    needsReactivation: boolean;
    isRealMode: boolean;
    isReactivating: boolean;
    agentAddress: string | null;
}

/**
 * Repairs a stale builder-code approval without involving the user.
 *
 * The approval needs no signature (RustSigner attests from the live session and a service wallet
 * submits), and a stale rate blocks nothing, so it used to be wrong on both counts to put the
 * whole "Reactivate your Snappy Wallet" ceremony in front of people for it. When the status
 * reports the rate as stale and the agent itself is fine, this asks the backend once per agent
 * per page load and refreshes the status afterwards. Mount ONCE (Trading.tsx), next to the prune
 * detection, for the same reason.
 */
export function useBuilderFeeApproval(input: UseBuilderFeeApprovalInput): void {
    const {
        builderApprovalStale,
        needsReactivation,
        isRealMode,
        isReactivating,
        agentAddress,
    } = input;
    const backend = useSpeedBackend();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (
            !isRealMode ||
            !builderApprovalStale ||
            // a real reactivation re-approves the builder on its own pass
            needsReactivation ||
            isReactivating ||
            !agentAddress ||
            !backend
        ) {
            return;
        }
        const key = agentAddress.toLowerCase();
        if (requestedForAgents.has(key)) return;
        requestedForAgents.add(key);
        backend
            .approveBuilderFee()
            .catch((error: unknown) => {
                // silent by design: trading works without a builder code, and the backend counts
                // the attempt so it does not loop
                console.warn('[BuilderFee] silent re-approval failed:', error);
            })
            .finally(() => {
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.speed.agentStatus(),
                });
            });
    }, [
        agentAddress,
        backend,
        builderApprovalStale,
        isReactivating,
        isRealMode,
        needsReactivation,
        queryClient,
    ]);
}
